import React, { useDeferredValue, useEffect, useMemo } from 'react';
import Card from './Card';
import { Transaction, Tag } from '../types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import CurrencyDisplay from './AmountDisplay';
import SpendingSummarySkeleton from './skeletons/SpendingSummarySkeleton';
import { useNavigation } from '../hooks/useNavigation';
import { getTopLevelTags, normalizeTagKey, normalizeTagName, UNTAGGED_KEY } from '../utils/tagHelpers';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { fetchTagsByType, selectTagMap } from '../store/slices/tagsSlice';
import { getCurrentKolkataYearMonth, parseTransactionDateParts } from '../utils/dateTimeUtils';

interface TagSpending {
  name: string;
  amount: number;
}

const SpendingSummary: React.FC = () => {
  const dispatch = useAppDispatch();
  const { transactions, status: transactionsStatus } = useAppSelector((state) => state.transactions);
  const { debitTags, status: tagsStatus, debitStatus } = useAppSelector((state) => state.tags);
  const tagMap = useAppSelector(selectTagMap);
  const deferredTransactions = useDeferredValue(transactions);
  const deferredDebitTags = useDeferredValue(debitTags);
  const deferredTagMap = useDeferredValue(tagMap);

  useEffect(() => {
    if (debitStatus === 'idle') {
      dispatch(fetchTagsByType('DEBIT'));
    }
  }, [debitStatus, dispatch]);

  const isLoading =
    transactionsStatus === 'loading' ||
    tagsStatus === 'loading' ||
    debitStatus === 'idle' ||
    debitStatus === 'loading';

  const spendingByTag = useMemo((): TagSpending[] => {
    if (transactionsStatus !== 'succeeded' || tagsStatus !== 'succeeded' || debitStatus !== 'succeeded') {
      return [];
    }

    const { year: currentYear, month: currentMonth } = getCurrentKolkataYearMonth();

    const currentMonthTransactions = deferredTransactions.filter((tx) => {
      const txDate = parseTransactionDateParts(tx.transactionDate);
      return txDate.year === currentYear && txDate.month === currentMonth;
    });

    const directSpending = new Map<string, number>();
    currentMonthTransactions.forEach((tx: Transaction) => {
      if (tx.type !== 'DEBIT' || tx.excludeFromAccounting || isInvestmentDebitTransaction(tx, deferredTagMap)) {
        return;
      }
      const key = normalizeTagKey(tx.tag);
      directSpending.set(key, (directSpending.get(key) ?? 0) + tx.amount);
    });

    const calculateTotalSpending = (tag: Tag): number => {
      const own = directSpending.get(normalizeTagName(tag.name)) ?? 0;
      const childrenTotal = (tag.children ?? []).reduce((sum, child) => sum + calculateTotalSpending(child), 0);
      return own + childrenTotal;
    };

    const aggregatedSpending: TagSpending[] = getTopLevelTags(deferredDebitTags)
      .map((tag) => ({ name: tag.name, amount: calculateTotalSpending(tag) }))
      .filter((item) => item.amount > 0);

    const untaggedTotal = directSpending.get(UNTAGGED_KEY) ?? 0;
    if (untaggedTotal > 0) {
      aggregatedSpending.push({ name: 'Untagged', amount: untaggedTotal });
    }

    aggregatedSpending.sort((a, b) => b.amount - a.amount);
    return aggregatedSpending;
  }, [deferredTransactions, deferredDebitTags, deferredTagMap, transactionsStatus, tagsStatus, debitStatus]);

  const { year: displayYear, month: displayMonth } = getCurrentKolkataYearMonth();
  const monthYear = `${new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, displayMonth - 1, 1))).toUpperCase()} ${displayYear}`;

  const totalSpending = useMemo(() => spendingByTag.reduce((sum, item) => sum + item.amount, 0), [spendingByTag]);

  const { navigateTo } = useNavigation();
  const handleCardClick = () => navigateTo('/spending-summary');

  return (
    <Card className="flex flex-col cursor-pointer" onClick={handleCardClick}>
      <div className="p-4 space-y-4 flex-grow overflow-y-auto bg-secondary">
        <div className="flex flex-row items-center text-muted-foreground justify-between">
          <h1 className="font-bold">Spending Summary</h1>
          <div className="text-xs font-semibold text-muted-foreground">
            {monthYear}
          </div>
        </div>

        <div className="space-y-2">
          {isLoading && <SpendingSummarySkeleton />}
          {transactionsStatus === 'succeeded' && spendingByTag.length === 0 && (
            <p className="text-muted-foreground text-center">No spending data for this period.</p>
          )}
          {!isLoading && spendingByTag.slice(0, 5).map((item, index) => {
            const barWidthPercentage = totalSpending > 0 ? (item.amount / totalSpending) * 100 : 0;
            return (
              <div key={index} className="flex justify-between items-center space-x-2">
                <div className="relative flex-1 min-w-0">
                  <div
                    className="absolute inset-y-0 left-0 bg-input/70 rounded-lg border border-border/50"
                    style={{ width: `${barWidthPercentage}%` }}
                    aria-hidden="true"
                  />
                  <span className="relative font-semibold text-card-foreground block truncate px-2 py-1">
                    {item.name}
                  </span>
                </div>
                <span className="font-medium text-card-foreground whitespace-nowrap">
                  <CurrencyDisplay amount={item.amount} className="font-bold" showType={false} showFraction={false} />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default SpendingSummary;
